import { AxiosResponse } from 'axios';
import apiClient from '../axios';
import {
  ICreateMemberParams,
  IUpdateMemberParams,
  Member,
  MemberBirthday,
  MemberOption,
  MembersSort,
  MemberShootingClearance,
  MemberShootingClearanceHistory,
  IGrantMemberShootingClearanceParams,
  IRevokeMemberShootingClearanceParams,
} from './interfaces';
import {
  IDeleteResourceResponse,
  IGetResourceCollectionResponse,
  IGetResourceResponse,
  IPaginatedResourceEndpointResponse,
  IUpsertSlugResourceResponse,
} from '../interfaces';
import { convertObjectToFormData } from '@helpers/apiHelper';
import { getPaginatedEndpointParams } from '@api/helpers';

export const getMembers = async (
  page: number,
  pageSize: number,
  sort?: MembersSort,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<Member>> => {
  const response: AxiosResponse<IPaginatedResourceEndpointResponse<Member>> =
    await apiClient.get('/manage/members', {
      params: getPaginatedEndpointParams({
        page,
        perPage: pageSize,
        sort,
        search,
      }),
    });

  return response.data;
};

export const getMemberOptions = async (
  page: number,
  pageSize: number,
  search?: string
): Promise<IPaginatedResourceEndpointResponse<MemberOption>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<MemberOption>
  > = await apiClient.get('/manage/members/options', {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      search,
    }),
  });

  return response.data;
};

export const getAllMemberOptions = async (): Promise<MemberOption[]> => {
  const response = await getMemberOptions(1, 0);

  return response.data;
};

export const getMember = async (slug: string): Promise<Member> => {
  const response: AxiosResponse<IGetResourceResponse<Member>> =
    await apiClient.get(`/manage/members/${slug}`);

  return response.data.data;
};

export const createMember = async (
  memberData: ICreateMemberParams
): Promise<string> => {
  const formData = convertObjectToFormData({
    ...memberData,
    dateOfBirth: memberData.dateOfBirth.format('YYYY-MM-DD'),
    ...(memberData.associationMemberSince && {
      associationMemberSince:
        memberData.associationMemberSince.format('YYYY-MM-DD'),
    }),
  });

  const response: AxiosResponse<IUpsertSlugResourceResponse> =
    await apiClient.post('/manage/members/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  return response.data.data.slug;
};

export const inviteToCreateAccount = async (slug: string): Promise<void> => {
  await apiClient.post(`/manage/members/${slug}/account`);
};

export const updateMember = async (
  memberSlug: string,
  memberData: IUpdateMemberParams
): Promise<string> => {
  const formattedFormData = {
    ...memberData,
    ...(memberData.dateOfBirth && {
      dateOfBirth: memberData.dateOfBirth.format('YYYY-MM-DD'),
    }),
    ...(memberData.associationMemberSince && {
      associationMemberSince:
        memberData.associationMemberSince.format('YYYY-MM-DD'),
    }),
  };

  const formData = convertObjectToFormData(formattedFormData, 'put');

  const response: AxiosResponse<IUpsertSlugResourceResponse> =
    await apiClient.post(`/manage/members/${memberSlug}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

  return response.data.data.slug;
};

export const deleteMember = async (
  memberSlug: string
): Promise<IDeleteResourceResponse> => {
  const response: AxiosResponse<IDeleteResourceResponse> =
    await apiClient.delete(`/manage/members/${memberSlug}`);

  return response.data;
};

export const getMemberShootingClearances = async (
  memberSlug: string
): Promise<MemberShootingClearance[]> => {
  const response: AxiosResponse<
    IGetResourceCollectionResponse<MemberShootingClearance>
  > = await apiClient.get(`/manage/members/${memberSlug}/shooting-clearances`);

  return response.data.data;
};

export const getMemberShootingClearanceHistory = async (
  memberSlug: string
): Promise<MemberShootingClearanceHistory[]> => {
  const response: AxiosResponse<
    IGetResourceCollectionResponse<MemberShootingClearanceHistory>
  > = await apiClient.get(
    `/manage/members/${memberSlug}/shooting-clearances/history`
  );

  return response.data.data;
};

export const grantMemberShootingClearance = async (
  memberSlug: string,
  params: IGrantMemberShootingClearanceParams
): Promise<MemberShootingClearance> => {
  const response: AxiosResponse<IGetResourceResponse<MemberShootingClearance>> =
    await apiClient.post(
      `/manage/members/${memberSlug}/shooting-clearances`,
      params
    );

  return response.data.data;
};

export const revokeMemberShootingClearance = async (
  memberSlug: string,
  params: IRevokeMemberShootingClearanceParams
): Promise<void> => {
  await apiClient.post(
    `/manage/members/${memberSlug}/shooting-clearances/revoke`,
    params
  );
};

export const getUpcomingBirthdaysOfMembers = async (): Promise<
  MemberBirthday[]
> => {
  const response: AxiosResponse<
    IGetResourceCollectionResponse<MemberBirthday>
  > = await apiClient.get('/members/upcoming-birthdays');

  return response.data.data;
};
